import React from "react";
import {injectIntl, IntlShape} from "react-intl";

import {ESopLicenses} from "@/components/license/models";
import {EDocumentTypes} from "@/components/project/models";
import networkDevices from "@/services/network-devices";
import {ICentSopModel} from "../models";

import Duration from "@shared/sop/Duration";
import UserLink from "@toolbox/building-blocks/UserLink";
import Card from "@toolbox/design/Card";
import Wavelength from "@toolbox/display-blocks/Wavelength";
import {hasSingleChannel} from "@toolbox/functions/device-check";
import {getDeviceFeatures} from "@toolbox/functions/license-functions";
import T from "@translate/T";
import Template from "./Template";
import {getNormalizationText, getNormalizationTooltip} from "./cycle-functions";

interface IGeneralProps {
    intl: IntlShape;
    sop: ICentSopModel;
    duration: number;
}

class General extends React.PureComponent<IGeneralProps> {
    public render() {
        return (
            <Card title={<T>General</T>}>
                <table className="table table-sm table-borderless">
                    <tbody>
                        {this.renderDevice()}
                        {this.renderDuration()}
                        {this.renderWavelength()}
                        {this.renderNormalizations()}
                        {this.renderTemplate()}
                        {this.renderACsv()}
                        {this.renderCreated()}
                    </tbody>
                </table>
            </Card>
        );
    }

    private renderDevice() {
        const {sop, intl} = this.props;

        return (
            <tr>
                <th className="text-right">
                    <T>Device</T>
                </th>
                <td id="device">
                    {networkDevices.getDeviceName(intl, sop.device)}
                </td>
            </tr>
        );
    }

    private renderDuration() {
        return (
            <tr>
                <th className="text-right">
                    <T>Duration</T>
                </th>
                <td id="duration">
                    <Duration seconds={this.props.duration} />
                </td>
            </tr>
        );
    }

    private renderWavelength() {
        const {sop} = this.props;

        if (hasSingleChannel(sop.device.deviceClass)) {
            return null;
        }

        return (
            <tr>
                <th className="text-right">
                    <T>Wavelengths</T>
                </th>
                <td id="wavelength">
                    <Wavelength wavelength={sop.wavelengths} />
                </td>
            </tr>
        );
    }

    private renderNormalizations() {
        const {intl, sop} = this.props;

        return (
            <tr>
                <th className="text-right">
                    <T>Normalizations</T>
                </th>
                <td title={getNormalizationTooltip(intl)}>
                    {getNormalizationText(sop)}
                </td>
            </tr>
        );
    }

    private renderTemplate() {
        const {device, templates} = this.props.sop;
        if (!getDeviceFeatures(device).includes(ESopLicenses.Template)) {
            return null;
        }

        return (
            <tr>
                <th className="text-right">
                    <T>Analysis template</T>
                </th>
                <td>
                    <Template selected={templates} />
                </td>
            </tr>
        );
    }

    private renderACsv() {
        const {acsv, device} = this.props.sop;
        if (!getDeviceFeatures(device).includes(ESopLicenses.ACsvExport)) {
            return null;
        }

        return (
            <tr>
                <th className="text-right">
                    <T>Automatic export</T>
                </th>
                <td>
                    {acsv?.on ? acsv.lockedPath ?? acsv.path : <T>Inactive</T>}
                </td>
            </tr>
        );
    }

    private renderCreated() {
        const {sop} = this.props;

        return (
            <tr>
                <th className="text-right">
                    <T>Created by</T>
                </th>
                <td>
                    <UserLink
                        username={sop.creator}
                        documentType={EDocumentTypes.User}
                    />
                </td>
            </tr>
        );
    }
}

export default injectIntl(General);
